import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/featureFlags";
import { useTenant } from "@/contexts/TenantContext";
import type { HotelProviderId } from "@waypoint/shared";

export type HotelProvider = HotelProviderId;

const isStagingEnvironment = (mode: string, hostname?: string): boolean =>
  mode === "staging" || (hostname?.startsWith("staging.") ?? false);

/**
 * Resolve the active hotel provider from config-driven overrides:
 * - a `stagingProvider` forces a provider in the staging environment,
 * - production defaults to Access Dev unless explicitly overridden, so a global
 *   Xeni flag cannot route live tenants to Xeni by accident,
 * - otherwise the Xeni feature flag selects "xeni", falling back to the
 *   tenant's configured fallback (default "access-dev").
 */
export const determineHotelProvider = ({
  xeniEnabled,
  hostname,
  mode,
  stagingProvider,
  productionProvider = "access-dev",
  fallbackProvider = "access-dev",
}: {
  xeniEnabled: boolean;
  hostname?: string;
  mode: string;
  stagingProvider?: HotelProviderId;
  productionProvider?: HotelProviderId;
  fallbackProvider?: HotelProviderId;
}): HotelProvider => {
  if (stagingProvider && isStagingEnvironment(mode, hostname)) {
    return stagingProvider;
  }
  if (mode === "production" && !isStagingEnvironment(mode, hostname)) {
    return productionProvider;
  }
  return xeniEnabled ? "xeni" : fallbackProvider;
};

export const useHotelProvider = (): HotelProvider => {
  const { tenant } = useTenant();
  const xeniEnabled = useFeatureFlag(FEATURE_FLAGS.HOTELS_XENI_ENABLED);
  return determineHotelProvider({
    xeniEnabled,
    hostname: typeof window === "undefined" ? undefined : window.location.hostname,
    mode: import.meta.env.MODE,
    stagingProvider: tenant.providers?.hotel?.staging,
    productionProvider: tenant.providers?.hotel?.production,
    fallbackProvider: tenant.providers?.hotel?.fallback,
  });
};
